'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { DM_Sans, Instrument_Serif } from 'next/font/google';
import { trackPostHogEvent } from '@/lib/utils/dataLayer';

const dmSans = DM_Sans({ 
  subsets: ['latin'],
  weight: ['400', '500', '700'],
});

const instrumentSerif = Instrument_Serif({ 
  subsets: ['latin'],
  weight: ['400'],
  style: ['italic'],
});

export default function Hero() {
  const router = useRouter();

  return (
    <section className="relative overflow-hidden px-4 lg:px-0 min-h-[650px] lg:min-h-[900px]">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <div className="relative w-full h-full">
          <div className="hidden lg:block absolute inset-0">
            <Image
              src="/images/homepage/hero.webp"
              alt=""
              fill
              className="rounded-[34px] object-cover"
              priority
            />
          </div>
          <div className="lg:hidden absolute top-4 left-4 right-4 bottom-0">
            <Image
              src="/images/homepage/hero.webp"
              alt=""
              fill
              className="object-cover rounded-[20px]"
              priority
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full pt-12 pb-0 min-h-[650px] lg:min-h-[900px] flex flex-col">
        {/* Heading block */}
        <div className="text-center max-w-4xl mx-auto mb-6 flex-shrink-0 px-4 lg:px-0">
          <div className="mb-6 hidden lg:block">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <span className="text-white text-sm font-medium">Productivity Tools and Services for Agencies</span>
            </div>
          </div>

          <h1 className="text-white mb-4 lg:mb-6 max-w-[689px] mx-auto px-4 lg:px-0 pt-8">
            <span className="text-3xl lg:text-[52px] font-normal leading-tight">
              The Smart Way to Run and Scale{' '}
            </span>
            <span
              className={`text-3xl lg:text-[56px] font-normal italic leading-[110%] ${instrumentSerif.className}`}
              style={{
                background: 'linear-gradient(116deg, #FFF 27.12%, #DBEDFF 111.75%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Your Agency
            </span>
          </h1>

          <p className="text-white/90 text-base lg:text-lg max-w-md lg:max-w-lg mx-auto mb-6 lg:mb-10 px-4 lg:px-0 font-bold">
            SmartMetrics helps marketing agencies save time, increase profit, and deliver a fully branded client experience
          </p>

          {/* CTAs */}
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="relative inline-flex">
              <div
                className="absolute inset-0 rounded-full opacity-60"
                style={{
                  background: 'linear-gradient(270deg, rgba(68, 255, 154, 0.60) 0%, rgba(68, 176, 255, 0.60) 23.56%, rgba(139, 68, 255, 0.60) 46.15%, rgba(255, 102, 68, 0.60) 67.31%, rgba(235, 255, 112, 0.60) 86.06%)',
                  filter: 'blur(15px)',
                }}
              />
              <button
                onClick={() => {
                  trackPostHogEvent('cta_click', {
                    cta_text: 'Access Agency Tools',
                    cta_location: 'hero',
                    cta_type: 'primary',
                  });
                  router.push('/agency');
                }}
                className={`relative flex h-[48px] px-7 py-[15px] justify-center items-center gap-[10px] rounded-full bg-white text-[#0478FB] text-center text-[18px] font-medium leading-none hover:bg-white/90 transition-colors ${dmSans.className}`}
              >
               Access Agency Tools
              </button>
            </div>

            <button
              onClick={() => {
                trackPostHogEvent('cta_click', {
                  cta_text: 'View Services',
                  cta_location: 'hero',
                  cta_type: 'secondary',
                });
                router.push('/services');
              }}
              className={`flex h-[48px] px-7 py-[15px] justify-center items-center gap-[10px] rounded-full border border-white/60 text-white text-center text-[18px] font-medium leading-none hover:bg-white/10 transition-colors ${dmSans.className}`}
            >
              View Services
            </button>
          </div>
        </div>

        {/* Services image panel */}
        <div className="relative w-full max-w-none md:max-w-[500px] lg:max-w-6xl mx-auto px-4 lg:px-12 xl:px-16 mt-auto flex-shrink-0 translate-y-[10px]">
          <div className="relative rounded-t-[30px] pt-[10px] px-[10px] bg-gradient-to-b from-white/30 to-transparent backdrop-blur-[10px]">
              <div className="relative w-full h-[320px] lg:h-[480px] overflow-hidden rounded-t-[20px]">
              <Image
                src="/images/features/hero.webp"
                alt="White Label Services"
                fill
                className="object-cover object-[center_top]"
                priority
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
